import { Component, effect, inject, OnInit, ApplicationRef, viewChild } from '@angular/core'
import { IonApp, IonRouterOutlet, NavController, Platform } from '@ionic/angular/standalone'
import { StoreSession } from './store/store-session'
import { LocalStorageService } from './store/services/local-storage.service'
import { CognitoService } from '@shared/services/cognito.service'
import { Preferences, Session } from './store/models/modelsStore'
import { LangService } from '@shared/services/lang.service'
import { SystemBars, SystemBarsStyle } from '@capacitor/core'
import { ToastService } from '@shared/services/toast.service'
import { AuthService } from '@session/services/auth.service'
import { AppFlowService } from '@shared/services/app-flow.service'
import { SessionActionsService } from '@shared/services/session-actions.service'
import { PushNotifications } from '@capacitor/push-notifications'
import { PushNotificationsService } from '@shared/services/push-notifications.service'
import { DeviceInfoService } from '@shared/services/device-info.service'
import { NotificationService } from '@shared/services/notification.service'
import { CrashlyticsService } from '@shared/services/crashlithycs.service'
import { SocialPluginService } from '@shared/services/social-plugin.service'
import { GlobalStore } from './store/global.store'
import { App } from '@capacitor/app'
import { serializeForCrashlytics } from 'utils/CrashlyticsSerializer'
import { MetaAnalyticsService } from '@shared/services/meta-analytics.service'
import { environment } from 'src/environments/environment'
import { catchError, of } from 'rxjs'

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {
  readonly store = inject(StoreSession)
  private platform = inject(Platform)
  private readonly localStorageService = inject(LocalStorageService)
  private readonly cognitoService = inject(CognitoService)
  private readonly langService = inject(LangService)
  private readonly appRef = inject(ApplicationRef)
  private readonly toastService = inject(ToastService)
  private readonly authService = inject(AuthService)
  private readonly appFlowService = inject(AppFlowService)
  private readonly sessionActionService = inject(SessionActionsService)
  private readonly pushNotificationsService = inject(PushNotificationsService)
  private readonly navController = inject(NavController)
  private readonly deviceInfoService = inject(DeviceInfoService)
  private readonly notificationService = inject(NotificationService)
  private readonly crashlyticsService = inject(CrashlyticsService)
  private readonly socialPluginService = inject(SocialPluginService)
  private readonly metaAnalyticsService = inject(MetaAnalyticsService)

  private routerOutlet = viewChild<IonRouterOutlet>('routerOutlet')
  public globalStore = inject(GlobalStore)

  counterStable = 0
  constructor() {
    effect(() => {
      const preferences: Preferences = this.store.preferences()
      const session: Session = this.store.session()
      this.localStorageService.saveStore({ preferences, session })
      this.initializeApp()
    })

    this.platform.ready().then(() => {
      this.configureStatusBar()
      this.deviceInfoService.initDeviceInfo()
      this.socialPluginService.initializeSocialLogin()
      this.metaAnalyticsService.initializeMetaAnalytics()
      App.addListener('backButton', () => {
        const routerOutlet = this.routerOutlet()
        if (routerOutlet && routerOutlet.canGoBack()) {
          this.navController.back()
        }
      })
    })

    this.initializeOpenUrlAppListener()
    this.crashlyticsService.log('App iniciada')
  }

  async ngOnInit() {
    this.langService.initLang()
    await this.addListenersNotifications()
    this.createChannel()
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.setLightTheme()
    })
  }

  setLightTheme() {
    document.body.classList.remove('dark-theme') // Ensure dark mode is removed
    document.body.classList.add('light-theme') // Apply light mode
  }

  async configureStatusBar() {
    try {
      setTimeout(async () => {
        // await StatusBar.setBackgroundColor({ color: '#ffffff' })
        // await StatusBar.setStyle({ style: Style.Light })
        // await StatusBar.setOverlaysWebView({ overlay: false })
        await SystemBars.setStyle({
          style: SystemBarsStyle.Light, // iconos oscuros para fondo claro
        })
      }, 1000)
    } catch (error) {
      console.error('Error configurando la Status Bar:', error)
    }
  }

  initializeOpenUrlAppListener() {
    // App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    //   //Listener when app is opened from a link
    // })
  }

  //Push notifications
  addListenersNotifications = async () => {
    await PushNotifications.addListener('registration', token => {
      this.pushNotificationsService.saveDeviceToken(token.value).subscribe(() => {
        this.store.changeDeviceToken(token.value)
      })
    })

    await PushNotifications.addListener('registrationError', err => {
      this.toastService.showToast(JSON.stringify(err))
    })

    await PushNotifications.addListener('pushNotificationReceived', notification => {
      const idNotification = notification.data?.idNotification
      try {
        this.notificationService.markNotificationAsReceived(idNotification).subscribe()
        this.notificationService.showNotificationDetails(notification.data)
        // console.log('notification: ', notification)

        const route = notification.data.route
        if (route && route === 'loan-reminder') {
          this.notificationService
            .trackNotificationAction(idNotification, {
              eventType: 'RECEIVED',
              occurredAt: new Date().toISOString(),
              metadata: {
                appVersion: environment.APP_VERSION,
                platform: 'Android',
              },
            })
            .subscribe()
        }
      } catch (error) {
        // console.log('error try catch: ', error)
        this.crashlyticsService.recordException('pushNotificationReceived - error', [
          {
            key: 'error',
            value: serializeForCrashlytics(error),
            type: 'string',
          },
          {
            key: 'description',
            value: 'Error al recibir la notificación push (received)',
            type: 'string',
          },
        ])
      } finally {
        this.appFlowService.getStateApp().subscribe()
        this.globalStore.setLastNotification(notification)
      }
    })

    await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
      try {
        this.notificationService.showNotificationDetails(notification.notification.data)

        const idNotification = notification.notification.data?.idNotification
        const route = notification.notification.data.route

        if (idNotification && route && route === 'loan-reminder') {
          this.notificationService
            .trackNotificationAction(idNotification, {
              eventType: 'OPENED',
              occurredAt: new Date().toISOString(),
              metadata: {
                appVersion: environment.APP_VERSION,
                platform: 'Android',
              },
            })
            .subscribe()
        }
      } catch (error) {
        this.crashlyticsService.recordException('pushNotificationActionPerformed - error', [
          {
            key: 'error',
            value: serializeForCrashlytics(error),
            type: 'string',
          },
          {
            key: 'description',
            value: 'Error al procesar la notificación push (performed)',
            type: 'string',
          },
        ])
      } finally {
        this.appFlowService.getStateApp().subscribe()
      }
    })
  }

  async createChannel() {
    await PushNotifications.createChannel({
      id: 'solventa-channel',
      name: 'solventa-channel',
      importance: 5,
      description: 'Canal para solventa',
      visibility: 1,
      vibration: true,
    })
  }
}
